import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  MessageSquare, RefreshCw, Send, Video, Image, FileText, Users,
  Clock, CheckCircle, Mail, Phone, Plus, Search, Star, ChevronRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FamilyPortalEnhanced = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('conversations');
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [showNewConversation, setShowNewConversation] = useState(false);
  const [newConversationData, setNewConversationData] = useState({
    patient_id: '', patient_name: '', family_member_name: '', subject: '', initial_message: ''
  });

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/phase67/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setDashboardData(await response.json());
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchConversation = async (conversationId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/phase67/conversations/${conversationId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedConversation(data.conversation);
        setMessages(data.messages);
      }
    } catch (error) {
      toast.error('Failed to load conversation');
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/phase67/conversations/${selectedConversation.id}/messages`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: newMessage, message_type: 'text' })
      });
      if (response.ok) {
        const message = await response.json();
        setMessages([...messages, message]);
        setNewMessage('');
        toast.success('Message sent');
      }
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const startConversation = async () => {
    if (!newConversationData.subject || !newConversationData.initial_message) {
      toast.error('Please fill in all required fields');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/phase67/conversations`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newConversationData,
          patient_id: newConversationData.patient_id || 'patient_' + Date.now(),
          patient_name: newConversationData.patient_name || 'Patient'
        })
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedConversation(data.conversation);
        setMessages([data.message]);
        setShowNewConversation(false);
        setNewConversationData({ patient_id: '', patient_name: '', family_member_name: '', subject: '', initial_message: '' });
        fetchDashboard();
        toast.success('Conversation started');
      }
    } catch (error) {
      toast.error('Failed to start conversation');
    }
  };

  const StatCard = ({ title, value, icon: Icon, color = 'blue' }) => (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-600 font-medium">{title}</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
          </div>
          <div className={`p-3 rounded-full bg-${color}-100`}>
            <Icon className={`h-6 w-6 text-${color}-600`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="family-portal-enhanced">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Family Communication Portal</h1>
              <p className="text-sm text-slate-600 mt-1 font-medium">Two-way messaging with families</p>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={fetchDashboard}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              <Button className="bg-teal-600 hover:bg-teal-700 text-white" onClick={() => setShowNewConversation(true)}>
                <Plus className="h-4 w-4 mr-2" />
                New Message
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
          <StatCard title="Conversations" value={dashboardData?.stats?.total_conversations || 0} icon={MessageSquare} color="blue" />
          <StatCard title="Unread" value={dashboardData?.stats?.unread_messages || 0} icon={Mail} color="red" />
          <StatCard title="This Week" value={dashboardData?.stats?.messages_this_week || 0} icon={Clock} color="green" />
          <StatCard title="Avg Response" value={dashboardData?.stats?.avg_response_time || '0h'} icon={CheckCircle} color="purple" />
          <StatCard title="Video Messages" value={dashboardData?.stats?.video_messages || 0} icon={Video} color="orange" />
          <StatCard title="Satisfaction" value={dashboardData?.stats?.family_satisfaction || 0} icon={Star} color="yellow" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Conversations List */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="text-slate-900 font-bold">Conversations</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="max-h-[600px] overflow-y-auto">
                {dashboardData?.recent_conversations?.map((conv) => (
                  <div
                    key={conv.id}
                    className={`p-4 border-b cursor-pointer hover:bg-gray-50 ${selectedConversation?.id === conv.id ? 'bg-teal-50 border-l-4 border-l-teal-500' : ''}`}
                    onClick={() => fetchConversation(conv.id)}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-semibold text-slate-900">{conv.patient_name}</p>
                        <p className="text-sm text-slate-600">{conv.family_member_name}</p>
                      </div>
                      {conv.unread_count > 0 && (
                        <Badge className="bg-red-500 text-white">{conv.unread_count}</Badge>
                      )}
                    </div>
                    <p className="text-sm font-medium text-slate-800 mb-1">{conv.subject}</p>
                    <p className="text-xs text-slate-500 truncate">{conv.last_message}</p>
                    <div className="flex items-center justify-between mt-2">
                      <Badge variant="outline" className={conv.status === 'active' ? 'text-green-600' : 'text-gray-500'}>
                        {conv.status}
                      </Badge>
                      <span className="text-xs text-slate-500">
                        {new Date(conv.last_message_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Message Thread */}
          <Card className="lg:col-span-2">
            <CardHeader className="border-b">
              {selectedConversation ? (
                <div>
                  <CardTitle className="text-slate-900 font-bold">{selectedConversation.subject}</CardTitle>
                  <CardDescription className="text-slate-600 font-medium">
                    {selectedConversation.patient_name} - {selectedConversation.family_member_name}
                  </CardDescription>
                </div>
              ) : (
                <CardTitle className="text-slate-500">Select a conversation</CardTitle>
              )}
            </CardHeader>
            <CardContent className="p-0">
              {selectedConversation ? (
                <>
                  {/* Messages */}
                  <div className="h-[400px] overflow-y-auto p-4 space-y-4">
                    {messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.sender_type === 'staff' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className={`max-w-[70%] p-3 rounded-lg ${
                          msg.sender_type === 'staff'
                            ? 'bg-teal-600 text-white'
                            : 'bg-gray-100 text-slate-900'
                        }`}>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-semibold opacity-80">{msg.sender_name}</span>
                            {msg.message_type === 'video' && <Video className="h-3 w-3" />}
                          </div>
                          <p className="text-sm">{msg.content}</p>
                          <span className="text-xs opacity-70 mt-1 block">
                            {new Date(msg.created_at).toLocaleTimeString()}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Input */}
                  <div className="p-4 border-t flex items-center gap-3">
                    <Button variant="outline" size="icon" title="Send Video">
                      <Video className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="icon" title="Attach File">
                      <FileText className="h-4 w-4" />
                    </Button>
                    <Input
                      placeholder="Type your message..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                      className="flex-1"
                    />
                    <Button className="bg-teal-600 hover:bg-teal-700 text-white" onClick={sendMessage}>
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </>
              ) : (
                <div className="h-[450px] flex items-center justify-center text-slate-500">
                  <div className="text-center">
                    <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p className="font-medium">Select a conversation to view messages</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* New Conversation Dialog */}
      <Dialog open={showNewConversation} onOpenChange={setShowNewConversation}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-slate-900 font-bold">New Conversation</DialogTitle>
            <DialogDescription className="text-slate-600">Start a new conversation with a family member</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="font-semibold text-slate-900">Patient Name</Label>
                <Input
                  placeholder="Enter patient name"
                  value={newConversationData.patient_name}
                  onChange={(e) => setNewConversationData({...newConversationData, patient_name: e.target.value})}
                />
              </div>
              <div>
                <Label className="font-semibold text-slate-900">Family Member</Label>
                <Input
                  placeholder="Enter family member name"
                  value={newConversationData.family_member_name}
                  onChange={(e) => setNewConversationData({...newConversationData, family_member_name: e.target.value})}
                />
              </div>
            </div>
            <div>
              <Label className="font-semibold text-slate-900">Subject *</Label>
              <Input
                placeholder="Enter subject"
                value={newConversationData.subject}
                onChange={(e) => setNewConversationData({...newConversationData, subject: e.target.value})}
              />
            </div>
            <div>
              <Label className="font-semibold text-slate-900">Message *</Label>
              <Textarea
                placeholder="Enter your message..."
                rows={4}
                value={newConversationData.initial_message}
                onChange={(e) => setNewConversationData({...newConversationData, initial_message: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewConversation(false)}>Cancel</Button>
            <Button className="bg-teal-600 hover:bg-teal-700 text-white" onClick={startConversation}>
              <Send className="h-4 w-4 mr-2" />
              Start Conversation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FamilyPortalEnhanced;
